import { AUDIT_TYPES } from './dataAudit'

const make = (chartKey, reason, suggestedMapping = {}, optionHints = []) => ({
  chartKey,
  reason,
  suggestedMapping,
  optionHints,
})

// Maps catalog chartTypes labels → rawgraphs chart IDs
const CATALOG_LABEL_TO_ID = {
  'Alluvial diagram':       'rawgraphs.alluvialdiagram',
  'Arc diagram':            'rawgraphs.arcdiagram',
  'Bar chart':              'rawgraphs.barchart',
  'Stacked bar':            'rawgraphs.barchartstacked',
  'Multiset bar':           'rawgraphs.barchartmultiset',
  'Beeswarm plot':          'rawgraphs.beeswarm',
  'Boxplot':                'rawgraphs.boxplot',
  'Bubble chart':           'rawgraphs.bubblechart',
  'Bump chart':             'rawgraphs.bumpchart',
  'Chord diagram':          'rawgraphs.chorddiagram',
  'Circle packing':         'rawgraphs.circlepacking',
  'Circular dendrogram':    'rawgraphs.circulardendrogram',
  'Contour plot':           'rawgraphs.contourplot',
  'Convex hull':            'rawgraphs.convexhull',
  'Dendrogram':             'rawgraphs.lineardendrogram',
  'Gantt chart':            'rawgraphs.ganttChart',
  'Hexagonal binning':      'rawgraphs.hexagonalbinning',
  'Hexbin map':             'rawgraphs.hexagonalbinning',
  'Horizon graph':          'rawgraphs.horizongraph',
  'Line chart':             'rawgraphs.linechart',
  'Matrix plot (heatmap)':  'rawgraphs.matrixplot',
  'Parallel coordinates':   'rawgraphs.parallelcoordinates',
  'Pie chart':              'rawgraphs.piechart',
  'Point map':              'rawgraphs.pointmap',
  'Radar chart':            'rawgraphs.radarchart',
  'Sankey':                 'rawgraphs.sankeydiagram',
  'Slope chart':            'rawgraphs.slopechart',
  'Streamgraph':            'rawgraphs.streamgraph',
  'Sunburst':               'rawgraphs.sunburst',
  'Treemap':                'rawgraphs.treemap',
  'Violin plot':            'rawgraphs.violinplot',
  'Voronoi':                'rawgraphs.voronoidiagram',
}

function firstColumn(entries, type) {
  const match = entries.find(([, info]) => info.type === type)
  return match ? match[0] : null
}

function columnsByType(entries, type) {
  return entries
    .filter(([, info]) => info.type === type)
    .map(([column]) => column)
}

export function getChartRecommendations(auditSummary, sampleChartTypes = []) {
  if (!auditSummary) return []
  const entries = Object.entries(auditSummary.schema || {})
  const byType = (t) => entries.filter(([, info]) => info.type === t)
  const categorical = byType(AUDIT_TYPES.CATEGORICAL)
  const numeric = byType(AUDIT_TYPES.NUMERIC)
  const datetime = byType(AUDIT_TYPES.DATETIME)
  const recs = []
  const firstCategorical = firstColumn(entries, AUDIT_TYPES.CATEGORICAL)
  const latitudeColumn = auditSummary.geoCandidates?.latitude?.column || null
  const longitudeColumn = auditSummary.geoCandidates?.longitude?.column || null
  const numericColumns = columnsByType(entries, AUDIT_TYPES.NUMERIC)
  const numericColumnsExcludingGeo = numericColumns.filter(
    (column) => column !== latitudeColumn && column !== longitudeColumn
  )
  const firstNumeric = numericColumns[0] || null
  const secondNumeric = numericColumns[1] || null
  const categoricalColumns = columnsByType(entries, AUDIT_TYPES.CATEGORICAL)
  const dateColumn = firstColumn(entries, AUDIT_TYPES.DATETIME)
  const firstGeoMeasure = numericColumnsExcludingGeo[0] || null

  if (latitudeColumn && longitudeColumn) {
    recs.push(
      make(
        'rawgraphs.pointmap',
        'Your dataset appears to include latitude and longitude fields, so plotting points on a map is a strong fit.',
        {
          latitude: [latitudeColumn],
          longitude: [longitudeColumn],
          size: firstGeoMeasure ? [firstGeoMeasure] : [],
        },
        ['Start with a world projection, then review point size and whether labels are needed.']
      )
    )
  }

  if (categorical.length >= 1 && numeric.length >= 1) {
    recs.push(
      make(
        'rawgraphs.barchart',
        'A categorical breakdown plus a numeric measure works well as bars.',
        {
          bars: firstCategorical ? [firstCategorical] : [],
          size: firstNumeric ? [firstNumeric] : [],
        },
        ['Set a title and brand colors, then check bar ordering and labels.']
      )
    )
  }
  if (datetime.length >= 1 && numeric.length >= 1) {
    recs.push(
      make(
        'rawgraphs.linechart',
        'A date/time field with a numeric measure is a natural trend line.',
        {
          x: dateColumn ? [dateColumn] : [],
          y: firstNumeric ? [firstNumeric] : [],
        },
        ['After mapping, review axis formatting and whether multiple lines are needed.']
      )
    )
  }
  if (numeric.length >= 2) {
    recs.push(
      make(
        'rawgraphs.bubblechart',
        'Two numeric measures can reveal correlation and clusters.',
        {
          x: firstNumeric ? [firstNumeric] : [],
          y: secondNumeric ? [secondNumeric] : [],
          size: columnsByType(entries, AUDIT_TYPES.NUMERIC)[2]
            ? [columnsByType(entries, AUDIT_TYPES.NUMERIC)[2]]
            : [],
        },
        ['Check point size and color options to keep the chart legible.']
      )
    )
  }
  if (categorical.length >= 2 && numeric.length >= 1) {
    recs.push(
      make(
        'rawgraphs.treemap',
        'Nested categories and one measure can be compared via area.',
        {
          hierarchy: categoricalColumns.slice(0, 2),
          size: firstNumeric ? [firstNumeric] : [],
        },
        ['Use short labels and review hierarchy depth before export.']
      )
    )
  }

  // Prepend chart types linked to the sample dataset, deduplicated against audit recs
  const sampleRecs = (sampleChartTypes || [])
    .map((label) => CATALOG_LABEL_TO_ID[label])
    .filter(Boolean)
    .filter((id, i, arr) => arr.indexOf(id) === i) // dedup labels that map to same id
    .map((chartKey) =>
      make(
        chartKey,
        'This chart type is paired with the sample dataset you loaded.',
        {},
        ['Map the required dimensions in the next step.']
      )
    )

  const auditChartKeys = new Set(sampleRecs.map((r) => r.chartKey))
  const filteredRecs = recs.filter((r) => !auditChartKeys.has(r.chartKey))

  return [...sampleRecs, ...filteredRecs].slice(0, 3)
}
